/**
 * CoursePage.js
 *
 * Standalone course-laying tool. No auth, no backend.
 *
 * Features:
 *  - Configure course type, wind direction, distance, etc. via CourseConfiguration
 *  - Calculate theoretical course marks from configuration
 *  - Display course on Google Maps via UnifiedCourseMap with live GPS position
 *  - Drop / lift physical marks at GPS position
 *  - Shareable URL: all configuration is encoded in query params, so sharing the
 *    browser URL recreates the exact course on any device instantly
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import CourseConfiguration from '../components/course/CourseConfiguration';
import {
  trackMapViewed,
  trackCourseCalculated,
  trackMarkDropped,
  trackMarkLifted,
  trackShareLinkCopied,
  trackStartPositionSet,
  trackCourseTypeSelected,
  trackPageView,
} from '../utils/analytics';
import UnifiedCourseMap, { convertToUnifiedMarks, MARK_DISPLAY_MODES } from '../components/course/UnifiedCourseMap';
import { calculateCourse, validateCourseConfiguration } from '../utils/courseCalculator';
import { readCourseStateFromUrl, writeCourseStateToUrl, enterMapView, buildShareUrl } from '../utils/courseUrlState';
import { MARK_STATUS } from '../utils/constants';
import TabBarIcon from '../components/TabBarIcon';
import fleetlyLogo from '../../public/favicon.png';
import '../styles/EditCourse.css';
import '../styles/ViewMap.css';

const VIEW_CONFIG = 0;
const VIEW_MAP    = 1;

function CoursePage() {
  // ── Initialise from URL params (shared link) ──────────────────────────────
  const { courseData: initCourseData, location: initLocation, offset: initOffset } =
    readCourseStateFromUrl();

  // ── State ─────────────────────────────────────────────────────────────────
  const [courseData, setCourseData] = useState(initCourseData);
  const [location,   setLocation]   = useState(initLocation);   // start mark position
  const [offset,     setOffset]     = useState(initOffset);

  const [courseMarks,    setCourseMarks]    = useState([]);
  const [boatLocation,   setBoatLocation]   = useState({ lat: 0, lng: 0 }); // live GPS
  const [viewState,      setViewState]      = useState(VIEW_CONFIG);
  const [error,          setError]          = useState('');
  const [selectedMark,   setSelectedMark]   = useState(null);
  const [markDisplayMode] = useState(MARK_DISPLAY_MODES.SHOW_IDEAL);
  const [mapControls,    setMapControls]    = useState(null);
  const [mapError,       setMapError]       = useState(false);
  const [linkCopied,     setLinkCopied]     = useState(false);

  // ── Auto-calculate when loaded directly at /map (e.g. a shared map link) ──
  useEffect(() => {
    if (window.location.pathname !== '/map') return;
    try {
      const raw = calculateCourse({
        numberOfBoats:        courseData.numberOfBoats,
        averageBoatLength:    courseData.averageBoatLength,
        startLineScaleFactor: courseData.startLineScaleFactor,
        windDirection:        courseData.windDirection,
        distance:             courseData.distance,
        leewardDistance:      courseData.leewardDistance,
        windwardBeats:        courseData.windwardBeats,
        gateWidth:            courseData.gateWidth,
        offset:               offset.enabled,
        offsetAngle:          offset.angle,
        offsetDistance:       offset.distance,
        finish:               true,
        finishDistance:       courseData.finishDistance,
        finishAngle:          courseData.finishAngle,
        finishType:           courseData.finishType,
        lat:                  location.lat,
        lng:                  location.lng,
        type:                 courseData.courseType,
        gate1:                courseData.gate1,
        gate3:                courseData.gate3,
        gate4:                courseData.gate4,
        spinnaker:            courseData.spinnaker,
        triangleAngle:        courseData.triangleAngle ?? 60,
      });
      setCourseMarks(convertToUnifiedMarks(raw));
      setViewState(VIEW_MAP);
    } catch (err) {
      setError(`Could not display shared course: ${err.message}`);
      writeCourseStateToUrl(courseData, location, offset); // restore /? path
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Track course type selections (skip the initial mount) ─────────────────
  const isFirstCourseTypeRender = useRef(true);
  useEffect(() => {
    if (isFirstCourseTypeRender.current) {
      isFirstCourseTypeRender.current = false;
      return;
    }
    trackCourseTypeSelected({ courseType: courseData.courseType });
  }, [courseData.courseType]);

  // ── Sync URL params whenever config or view state changes ────────────────
  const urlTimer = useRef(null);
  useEffect(() => {
    clearTimeout(urlTimer.current);
    if (viewState === VIEW_MAP) {
      // /map?course-params — no lat/lng (GDPR); GA4 sees a distinct page
      enterMapView(courseData, offset);
    } else {
      // /?full-params — debounced to avoid thrashing while user types
      urlTimer.current = setTimeout(() => {
        writeCourseStateToUrl(courseData, location, offset);
      }, 300);
    }
    return () => clearTimeout(urlTimer.current);
  }, [viewState, courseData, location, offset]);

  // ── Listen for GPS updates dispatched by App.js ───────────────────────────
  useEffect(() => {
    const onGps = (e) => {
      const d = e.detail;
      if (d?.lat && d?.lng) {
        setBoatLocation({ lat: parseFloat(d.lat), lng: parseFloat(d.lng) });
      }
    };
    window.addEventListener('gpsUpdate', onGps);
    return () => window.removeEventListener('gpsUpdate', onGps);
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleLayTheCourse = () => {
    const errs = validateCourseConfiguration({
      lat: location.lat,
      lng: location.lng,
      numberOfBoats:    courseData.numberOfBoats,
      averageBoatLength: courseData.averageBoatLength,
      windDirection:    courseData.windDirection,
      distance:         courseData.distance,
      leewardDistance:  courseData.leewardDistance,
      windwardBeats:    courseData.windwardBeats
    });
    if (errs.length > 0) {
      setError(errs.join('. '));
      return;
    }

    try {
      const raw = calculateCourse({
        numberOfBoats:       courseData.numberOfBoats,
        averageBoatLength:   courseData.averageBoatLength,
        startLineScaleFactor: courseData.startLineScaleFactor,
        windDirection:       courseData.windDirection,
        distance:            courseData.distance,
        leewardDistance:     courseData.leewardDistance,
        windwardBeats:       courseData.windwardBeats,
        gateWidth:           courseData.gateWidth,
        offset:              offset.enabled,
        offsetAngle:         offset.angle,
        offsetDistance:      offset.distance,
        finish:              true,
        finishDistance:      courseData.finishDistance,
        finishAngle:         courseData.finishAngle,
        finishType:          courseData.finishType,
        lat:                 location.lat,
        lng:                 location.lng,
        type:                courseData.courseType,
        gate1:               courseData.gate1,
        gate3:               courseData.gate3,
        gate4:               courseData.gate4,
        spinnaker:           courseData.spinnaker,
        triangleAngle:       courseData.triangleAngle ?? 60
      });

      const unifiedMarks = convertToUnifiedMarks(raw);
      setCourseMarks(unifiedMarks);
      setError('');
      trackCourseCalculated({
        courseType:    courseData.courseType,
        windDirection: courseData.windDirection,
        distance:      courseData.distance,
      });
      trackMapViewed({
        courseType: courseData.courseType,
        markCount:  unifiedMarks.length,
      });
      enterMapView(courseData, offset);
      trackPageView('Map View');
      setViewState(VIEW_MAP);
    } catch (err) {
      setError(`Course calculation failed: ${err.message}`);
    }
  };

  const setStartPosition = () => {
    if (boatLocation.lat === 0 && boatLocation.lng === 0) {
      setError('Current GPS position not available. Please enable location services.');
      return;
    }
    setLocation({ lat: boatLocation.lat, lng: boatLocation.lng });
    trackStartPositionSet();
    setError('');
  };

  const copyShareLink = useCallback(() => {
    const shareUrl = buildShareUrl(courseData, location, offset);
    trackShareLinkCopied();
    navigator.clipboard.writeText(shareUrl).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    }).catch(() => {
      window.prompt('Copy this link:', shareUrl);
    });
  }, [courseData, location, offset]);

  // Drop/lift a physical mark at current GPS position
  const handleMarkDrop = () => {
    if (!selectedMark) return;
    if (boatLocation.lat === 0 && boatLocation.lng === 0) {
      setError('GPS position not available to drop mark.');
      return;
    }
    const isLifting = selectedMark.isActualSet;
    const updated = {
      ...selectedMark,
      actualLat:  isLifting ? selectedMark.lat : boatLocation.lat,
      actualLng:  isLifting ? selectedMark.lng : boatLocation.lng,
      isActualSet: !isLifting,
      status: isLifting ? MARK_STATUS.LIFTED : MARK_STATUS.DROPPED
    };
    if (isLifting) {
      trackMarkLifted({ markLabel: selectedMark.label });
    } else {
      trackMarkDropped({ markLabel: selectedMark.label });
    }
    setCourseMarks(prev => prev.map(m => m.key === selectedMark.key ? updated : m));
    setSelectedMark(updated);
  };

  const hasGps = boatLocation.lat !== 0 || boatLocation.lng !== 0;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="edit-course-main-section">

      {/* ── Configuration View ─────────────────────────────────────────────── */}
      {viewState === VIEW_CONFIG && (
        <>
          <div className="edit-course-header">
            <div className="course-context">
              <img
                src={fleetlyLogo}
                alt="Fleetly"
                style={{ height: '32px', width: 'auto' }}
              />
              <h3>Fleetly - Lay The Course</h3>
            </div>
            <div className="header-actions">
              <Link
                to="/manual"
                className="manual-link secondary-action"
                title="Open user manual"
              >
                <TabBarIcon name="book" />
                <span>Manual</span>
              </Link>
              <button
                className="share-button secondary-action"
                onClick={copyShareLink}
                title="Copy shareable link to clipboard"
              >
                <TabBarIcon name={linkCopied ? 'check' : 'globe'} />
                <span>{linkCopied ? 'Copied!' : 'Share Link'}</span>
              </button>
            </div>
          </div>

          {error && (
            <div className="form-error">
              <TabBarIcon name="exclamation-circle" color="#F44336" />
              <span>{error}</span>
            </div>
          )}

          <div className="course-configuration">
            <CourseConfiguration
              courseData={courseData}
              setCourseData={setCourseData}
              offset={offset}
              setOffset={setOffset}
            />

            <div className="position-info">
              <div className="position-display">
                <strong>Start Position:</strong>
                <span className={location.lat === 59.324 && location.lng === 18.071 ? 'no-position' : ''}>
                  {' '}{location.lat.toFixed(6)}, {location.lng.toFixed(6)}
                </span>
              </div>
              <div className="position-display">
                <strong>Current GPS:</strong>
                {hasGps
                  ? <span> {boatLocation.lat.toFixed(6)}, {boatLocation.lng.toFixed(6)}</span>
                  : <span className="no-position"> Waiting for GPS...</span>
                }
              </div>
            </div>

            <div className="course-actions">
              <button
                className="set-position-button"
                onClick={setStartPosition}
                disabled={!hasGps}
                title={hasGps ? 'Set start mark at current GPS position' : 'Waiting for GPS fix'}
              >
                <TabBarIcon name="map-marker-alt" />
                <span>Set Start Position</span>
              </button>

              <button
                className="lay-course-button"
                onClick={handleLayTheCourse}
              >
                <TabBarIcon name="route" />
                <span>Lay The Course</span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Map View ───────────────────────────────────────────────────────── */}
      {viewState === VIEW_MAP && (
        <div
          className="view-map-container"
          style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column' }}
        >
          {/* Map toolbar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '6px 10px', background: '#fff',
            borderBottom: '1px solid #ddd', flexShrink: 0
          }}>
            <button
              className="back-button"
              onClick={() => { trackPageView('Course Configuration'); setViewState(VIEW_CONFIG); setSelectedMark(null); }}
            >
              <TabBarIcon name="arrow-left" />
              <span>Back</span>
            </button>

            <button
              className="secondary-action share-button"
              onClick={copyShareLink}
              title="Copy shareable link"
            >
              <TabBarIcon name={linkCopied ? 'check' : 'globe'} />
              <span>{linkCopied ? 'Copied!' : 'Share'}</span>
            </button>

            {mapControls?.resetAutoFit && (
              <button onClick={mapControls.resetAutoFit} title="Reset map to fit all marks">
                <TabBarIcon name="compress" />
                <span>Fit</span>
              </button>
            )}

            {selectedMark && (
              <button
                className="warning-action"
                onClick={handleMarkDrop}
                disabled={!hasGps}
                title={
                  selectedMark.isActualSet
                    ? `Lift ${selectedMark.label} back to ideal position`
                    : `Drop ${selectedMark.label} at current GPS position`
                }
              >
                <TabBarIcon name={selectedMark.isActualSet ? 'arrow-up' : 'anchor'} />
                <span>{selectedMark.isActualSet ? 'Lift' : 'Drop'} {selectedMark.label}</span>
              </button>
            )}

            {error && (
              <span style={{ color: '#F44336', fontSize: '12px', marginLeft: 'auto' }}>
                {error}
              </span>
            )}
          </div>

          {/* Map */}
          <div style={{ flex: 1, overflow: 'hidden' }}>
            {!mapError ? (
              <UnifiedCourseMap
                marks={courseMarks}
                boats={[]}
                userLocation={boatLocation}
                markDisplayMode={markDisplayMode}
                showBoats={false}
                showBoatTrails={false}
                showUserLocation={true}
                allowUserInteraction={true}
                autoFitBounds={true}
                onMarkClick={setSelectedMark}
                onSelectedMarkChange={setSelectedMark}
                selectedMark={selectedMark}
                allowMarkDrop={true}
                allowMarkLift={true}
                onMapReady={setMapControls}
                height="100%"
                onError={() => setMapError(true)}
              />
            ) : (
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <TabBarIcon name="exclamation-triangle" color="#f44336" />
                <p>Map encountered an error.</p>
                <button onClick={() => setMapError(false)}>
                  <TabBarIcon name="redo" /> Retry
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CoursePage;
